import {
  Layers, FileText, Plus, Pencil, Save, ShieldCheck,
  Database, Search, FolderSearch, Wrench, Ticket, FilePlus, FilePen,
} from 'lucide-react'

/**
 * Human-friendly presentation for each tool the builder agent can call.
 *
 * The chat surfaces raw MCP tool names like `mcp__orbit-builder__create_block_def`,
 * which mean nothing to a non-technical editor. Each descriptor turns a
 * (name, input) pair into an icon + a short French phrase naming the action and
 * its salient argument — e.g. `Création du bloc « Pull Quote »`. The phrase is
 * status-agnostic: the spinner / check / cross icon in ToolUseCard carries the
 * pending / done / error state, so the wording stays in the present and never
 * has to be re-tensed.
 *
 * `input` may be `{}` for the brief window between a tool block starting to
 * stream and its arguments landing, so every accessor must tolerate missing
 * fields (the `q` helper simply omits the suffix when the value is absent).
 */

// ` « value »` when present, '' otherwise — lets a phrase append its argument
// without ever dangling an empty quote.
const q = (v) => (v == null || v === '') ? '' : ` « ${v} »`

// Last path segment, for Read's file_path.
const base = (p) => (typeof p === 'string' && p ? p.split('/').filter(Boolean).at(-1) : '')

// First 8 chars of a uuid — enough to recognise, short enough to read.
const short = (u) => (typeof u === 'string' && u ? u.slice(0, 8) : '')

const DESCRIPTORS = {
  // Block defs ------------------------------------------------------------
  list_block_defs: { icon: Layers, text: (i) => 'Catalogue de blocs' + (i.search ? ` · « ${i.search} »` : '') },
  get_block_def: { icon: FileText, text: (i) => `Lecture du bloc${q(i.type ?? i.nid)}` },
  create_block_def: { icon: Plus, text: (i) => `Création du bloc${q(i.title ?? i.type)}` },
  update_block_def: { icon: Pencil, text: (i) => `Mise à jour du bloc${q(i.title ?? i.nid)}` },

  // Object defs (typed content schemas) -----------------------------------
  list_object_defs: { icon: Database, text: (i) => 'Types de contenu' + (i.search ? ` · « ${i.search} »` : '') },
  get_object_def: { icon: FileText, text: (i) => `Lecture du type${q(i.type)}` },
  save_object_def: { icon: Save, text: (i) => `Enregistrement du type${q(i.name ?? i.type)}` },

  // Elasticsearch query nodes ---------------------------------------------
  list_elasticsearch_queries: { icon: Database, text: (i) => 'Requêtes Elasticsearch' + (i.search ? ` · « ${i.search} »` : '') },
  get_elasticsearch_query: { icon: FileText, text: (i) => `Lecture d'une requête ES${q(short(i.uuid))}` },
  save_elasticsearch_query: { icon: Save, text: (i) => `Enregistrement d'une requête ES${q(i.title)}` },

  // Validation ------------------------------------------------------------
  validate_rj_build: { icon: ShieldCheck, text: () => 'Validation de la structure rjBuild' },

  // Ticket proposal --------------------------------------------------------
  // Rendered as a button by MessageList, not as a tool card; this descriptor is
  // what the exported transcript and any fallback rendering use.
  propose_ticket: { icon: Ticket, text: (i) => `Proposition de ticket${q(i.title)}` },

  // Built-in filesystem tools ---------------------------------------------
  Read: { icon: FileText, text: (i) => `Lecture${q(base(i.file_path))}` },
  Glob: { icon: FolderSearch, text: (i) => `Fichiers${q(i.pattern)}` },
  Grep: { icon: Search, text: (i) => `Recherche${q(i.pattern)}` },
  // Les deux SEULS outils qui modifient le disque. Ils n'existent que si
  // l'éditeur a coché l'autorisation, et le chemin est ce qu'il faut lire pour
  // savoir ce qui a été touché : il est donné en entier, jamais réduit au nom
  // de fichier comme pour une lecture.
  Write: { icon: FilePlus, text: (i) => `Écriture${q(i.file_path)}` },
  Edit: { icon: FilePen, text: (i) => `Modification${q(i.file_path)}` },
}

/** Strip the MCP namespace prefixes for a bare tool name. */
export function bareName(name) {
  return String(name ?? '').replace(/^mcp__orbit-builder__/, '').replace(/^mcp__/, '')
}

/**
 * Resolve a tool call to `{ icon, text }`. Unknown tools fall back to a wrench
 * icon and their bare name, so a newly added tool degrades gracefully instead
 * of throwing.
 */
export function describeTool(name, input = {}) {
  const key = bareName(name)
  const d = DESCRIPTORS[key]
  const safeInput = input && typeof input === 'object' ? input : {}
  if (!d) return { icon: Wrench, text: key }
  let text
  try { text = d.text(safeInput) }
  catch { text = key }
  return { icon: d.icon, text: text || key }
}
